import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { fetchUserProfile, changePassword } from '../../services/authApi';
import styles from './ProfilePage.module.css';

// Components
import ProfileHeader from './components/ProfileHeader';
import UserDetails from './components/UserDetails';
import PasswordChange from './components/PasswordChange';
import BillingAddress from './components/BillingAddress';

const ProfilePage = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();

    const [userData, setUserData] = useState({ username: '', email: '', companyname: '', fullname: '', });
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [passwords, setPasswords] = useState({
        current: '',
        newPassword: '',
        confirmPassword: '',
    });

    // Fetch user data on mount
    useEffect(() => {
        const loadUserProfile = async () => {
            if (!authCtx.token) {
                setError("Authentication error. Please log in again.");
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            setError(null);
            try {
                const profileData = await fetchUserProfile(authCtx.token);
                setUserData({
                    username: profileData.username || 'N/A',
                    email: profileData.email || 'N/A',
                    fullname: profileData.fullname || 'N/A',
                    companyname: profileData.companyname || 'N/A',
                    amazon_status: profileData.amazon_status || 'N/A',
                });
            } catch (err) {
                setError(err.message || err.data?.error || "Failed to load profile data.");
            } finally {
                setIsLoading(false);
            }
        };

        loadUserProfile();
    }, [authCtx.token]);

    
    

    // Helper function to validate email format
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Handle password input changes
    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
        setError(null);
        setSuccessMessage('');
    };

    // Toggle password section visibility
    const togglePasswordFields = () => {
        setShowPasswordFields(!showPasswordFields);
        setPasswords({ current: '', newPassword: '', confirmPassword: '' });
        setError(null);
        setSuccessMessage('');
    };

    // Handle submitting the new password
    const handlePasswordSave = async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage('');

        // Check if we have a valid email
        if (!userData.email || userData.email === 'N/A' || !isValidEmail(userData.email)) {
            setError("Email address is required.");
            return;
        }

        if (passwords.newPassword !== passwords.confirmPassword) {
            setError("New password and confirm password do not match.");
            return;
        }
        if (!passwords.current || !passwords.newPassword) {
            setError("All password fields are required.");
            return;
        }
        if (passwords.newPassword.length < 8) {
            setError("New password must be at least 8 characters long.");
            return;
        }

        setIsUpdating(true);
        try {
            // Pass the email along with the password change request
            const response = await changePassword(
                authCtx.token, 
                passwords.current, 
                passwords.newPassword,
                userData.email // Include email in the request
            );
            setSuccessMessage(response.message || "Password updated successfully!");
            setPasswords({ current: '', newPassword: '', confirmPassword: '' });
            setShowPasswordFields(false);
        } catch (err) {
            setError(err.message || err.data?.error || "Failed to change password.");
        } finally {
            setIsUpdating(false);
        }
    };

    if (isLoading) {
        return <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading your profile...</p>
        </div>;
    }

    return (
        <div className={styles.profileContainer}>
            <div className={styles.profileCard}>

                {/* Header */}
                <ProfileHeader username={userData.username} fullname={userData.fullname} styles={styles} />

                {/* User Info Section */}
                <UserDetails userData={userData} />

                {/* Billing Address Section */}
                <BillingAddress />

                {/* Change Password Section */}
                <PasswordChange
                    error={error}
                    successMessage={successMessage}
                    showPasswordFields={showPasswordFields}
                    togglePasswordFields={togglePasswordFields}
                    handlePasswordSave={handlePasswordSave}
                    handlePasswordChange={handlePasswordChange}
                    passwords={passwords}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    isUpdating={isUpdating}
                />

            </div>
        </div>
    );
};

export default ProfilePage;